import { useCallback } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';

import type { EligibilitySnapshot, StatusLight } from '../../types';

export type VerificationMethod = 'portal' | 'phone' | '';

interface ManualOverrideFooterProps {
  snapshot: EligibilitySnapshot;
  isOverrideMode: boolean;
  verificationMethod: VerificationMethod;
  onToggleOverride: () => void;
  onOverrideChange: (field: 'status_light' | 'summary', value: string) => void;
  onMethodChange: (method: VerificationMethod) => void;
  onBlur?: () => void;
}

const STATUS_LIGHT_OPTIONS: { value: StatusLight; label: string }[] = [
  { value: 'GREEN', label: 'GREEN' },
  { value: 'YELLOW', label: 'YELLOW' },
  { value: 'RED', label: 'RED' },
];

const METHOD_OPTIONS: { value: VerificationMethod; label: string }[] = [
  { value: 'portal', label: 'Payer portal (e.g., Availity)' },
  { value: 'phone', label: 'Phone call to payer' },
];

function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    const absolute = format(date, 'MMM d, yyyy h:mm a');
    const relative = formatDistanceToNow(date, { addSuffix: true });
    return `${absolute} (${relative})`;
  } catch {
    return isoString;
  }
}

export function ManualOverrideFooter({
  snapshot,
  isOverrideMode,
  verificationMethod,
  onToggleOverride,
  onOverrideChange,
  onMethodChange,
  onBlur,
}: ManualOverrideFooterProps) {
  const handleStatusChange = useCallback(
    (event: SelectChangeEvent) => {
      onOverrideChange('status_light', event.target.value);
    },
    [onOverrideChange],
  );

  const handleSummaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onOverrideChange('summary', event.target.value);
    },
    [onOverrideChange],
  );

  const handleMethodChange = useCallback(
    (event: SelectChangeEvent) => {
      onMethodChange(event.target.value as VerificationMethod);
    },
    [onMethodChange],
  );

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />

      {/* Timestamps */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Last checked: {formatTimestamp(snapshot.last_checked_at)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Expires: {formatTimestamp(snapshot.expires_at)}
        </Typography>
      </Box>

      {/* Override prompt + inline form */}
      {!isOverrideMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Already verified this manually?
          </Typography>
          <Button size="small" onClick={onToggleOverride}>
            Update status
          </Button>
        </Box>
      )}

      <Collapse in={isOverrideMode} unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" variant="contained" onClick={onToggleOverride}>
                Done
              </Button>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Status
              </Typography>
              <Select
                value={snapshot.status_light}
                onChange={handleStatusChange}
                size="small"
                fullWidth
              >
                {STATUS_LIGHT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                How did you verify?
              </Typography>
              <Select
                value={verificationMethod}
                onChange={handleMethodChange}
                size="small"
                fullWidth
                displayEmpty
              >
                <MenuItem value="">
                  <Typography color="text.secondary">Select method...</Typography>
                </MenuItem>
                {METHOD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Summary
              </Typography>
              <TextField
                value={snapshot.summary}
                onChange={handleSummaryChange}
                onBlur={onBlur}
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder="Describe what you verified and the outcome..."
              />
            </Box>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
